/**
 * The date key: `YYYY-MM-DD` in the application timezone.
 *
 * This is the primary partition for almost every record in the product, so it
 * gets exactly one implementation and one validator. The old codebase had the
 * format re-implemented inline in at least ten places and the validating regex
 * declared four times under three different names, which is why two independent
 * date-key implementations could run side by side in the same server process.
 *
 * Audit references prevented here:
 *   utils.js #9        (the format re-implemented in ten places)
 *   training.js #12    (fourth copy of the validating regex)
 *   server.js #26      (a tenth implementation, using server-local time)
 *   training.jsx #7    (two date-key functions distinguished only by a comment)
 */

import { APP_TZ } from './app-timezone.js';
import { zonedParts } from './zoned-parts.js';

/** Shape of a valid date key. Anchored, so no partial match slips through. */
export const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Number of characters in a date key. Used by callers that slice keys. */
export const DATE_KEY_LENGTH = 10;

function pad2(value) {
  return String(value).padStart(2, '0');
}

/**
 * The calendar date an instant falls on, in the application timezone.
 *
 * @param {Date} instant
 * @param {string} [timeZone]  Overridable for tests only. Product code omits it.
 * @returns {string} For example "2026-08-03".
 * @throws {TypeError} if `instant` is not a valid Date.
 */
export function dateKeyOf(instant, timeZone = APP_TZ) {
  const { year, month, day } = zonedParts(instant, timeZone);
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Whether a value is a syntactically valid date key.
 *
 * Shape only. It does not reject an impossible calendar date such as
 * "2026-02-31"; use {@link assertDateKey} at a trust boundary for that.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isDateKey(value) {
  return typeof value === 'string' && DATE_KEY_PATTERN.test(value);
}

/**
 * Anchor instant for a date key: 12:00 UTC on that calendar date.
 *
 * Noon UTC is chosen deliberately. Every zone the product cares about sits
 * within roughly twelve hours of UTC, so a noon anchor cannot land on the
 * neighbouring calendar date, which is the class of off-by-one-day bug the old
 * `localDateKey` versus `dkey` pairing existed to work around.
 *
 * Read this instant back with `timeZone: 'UTC'`. Reading it in the application
 * timezone gives the same calendar date but an early-morning wall clock, so it
 * must never be used to represent a time of day.
 *
 * @param {string} dateKey
 * @returns {Date}
 * @throws {RangeError} if the key is not a real calendar date.
 */
export function anchorInstantForDateKey(dateKey) {
  assertDateKey(dateKey);
  const year = Number(dateKey.slice(0, 4));
  const month = Number(dateKey.slice(5, 7));
  const day = Number(dateKey.slice(8, 10));
  const instant = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
  // Date.UTC rolls an impossible date forward (February 31 becomes March 3), so
  // round-trip it and reject rather than accept a silently shifted day.
  if (instant.getUTCMonth() !== month - 1 || instant.getUTCDate() !== day) {
    throw new RangeError(`Not a real calendar date: ${dateKey}`);
  }
  return instant;
}

/**
 * Whether a value names a real calendar date, not merely something the right shape.
 *
 * {@link isDateKey} accepts "2026-02-31" because it only checks the shape, and
 * {@link anchorInstantForDateKey} throws on it. Neither is what a form wants: a date input
 * can be cleared or, on a browser without one, typed into freely, and a screen reading
 * that value has to decide whether to use it without a `try` around every render.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isCalendarDateKey(value) {
  if (!isDateKey(value)) return false;
  try {
    anchorInstantForDateKey(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * The date key a whole number of days from this one. Negative amounts move back.
 *
 * Lives here rather than beside the first feature that needed it. It was written in
 * `domain/training/program.js`, which meant the calendar and now vitamins had to import a
 * training module to add a day to a date, and the alternative was a fifth hand-rolled copy
 * of the same three lines. Going through the noon anchor also means a bad key is rejected
 * here instead of quietly producing "NaN-NaN-NaN".
 *
 * @param {string} dateKey
 * @param {number} amount  Whole days.
 * @returns {string}
 * @throws {TypeError|RangeError} if `dateKey` is not a real calendar date.
 */
export function addDateKeyDays(dateKey, amount) {
  const anchor = anchorInstantForDateKey(dateKey);
  anchor.setUTCDate(anchor.getUTCDate() + amount);
  return `${anchor.getUTCFullYear()}-${pad2(anchor.getUTCMonth() + 1)}-${pad2(anchor.getUTCDate())}`;
}

/**
 * Assert that a value is a valid date key.
 *
 * @param {unknown} value
 * @returns {string} the same value, narrowed.
 * @throws {TypeError} if the shape is wrong.
 */
export function assertDateKey(value) {
  if (!isDateKey(value)) {
    throw new TypeError(`Expected a YYYY-MM-DD date key, received ${JSON.stringify(value)}`);
  }
  return value;
}
